import React, { useEffect, useRef } from "react";
import * as R from "ramda";
import { useSettings } from "utils/settingsContext";
import * as utils from "utils";
import { logInfo, logError } from "api/accounts-customer";
import { WebSDK } from "./WebSDK";

const pre = "[KOUNT]";

const isKountEnabled = (settings) => utils.isTrue(R.path(["feature_flags", "kount"], settings));
const isLogSuccess = (settings) => utils.isTrue(R.path(["kount", "log_success"], settings));

// returns 0 if the value is not a number
const getKountTimeout = (settings) => {
  const val = R.path(["kount", "callbackTimeoutMS"], settings);
  return R.is(Number, val) ? val : 0;
};

export const Kount = ({ onComplete = utils.noop, onError = utils.noop }) => {
  const { debug, sessionId } = utils.useSessionCtx();
  const { sessionJWT } = utils.useSessionJWT();
  const { appSettings } = useSettings();
  const timeoutId = useRef(null);

  const timeoutMS = getKountTimeout(appSettings);

  // On mount
  useEffect(() => {
    if (utils.isNilOrEmpty(appSettings)) return;

    if (isKountEnabled(appSettings)) {
      if (timeoutMS > 0) {
        timeoutId.current = setTimeout(() => {
          debug(pre, "kount timeout", timeoutMS);
          logError(sessionJWT, {
            flowLoc: "KOUNT",
            error: `timeout processing kount: ${timeoutMS}`,
            sessionId,
          });

          onComplete();
        }, timeoutMS);

        debug(pre, "starting timeout", timeoutId.current);

        return () => {
          clearTimeout(timeoutId.current);
        };
      }
    } else {
      // kount configured off, call onComplete
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = async (resultData) => {
    debug(pre, "complete:", resultData);
    if (timeoutId.current) {
      debug(pre, "clearing timeout", timeoutId.current);
      clearTimeout(timeoutId.current);
    }
    if (isLogSuccess(appSettings)) {
      await logInfo(sessionJWT, {
        flowLoc: "KOUNT",
        resultData,
      });
    }
    onComplete();
  };

  const handleError = (error, resultData) => {
    debug(pre, "error:", error, resultData);
    logError(sessionJWT, {
      flowLoc: "KOUNT",
      error: `error processing kount: ${error}`,
      resultData,
      sessionId,
    });
    onError(error);
  };

  if (!isKountEnabled(appSettings)) return null;
  if (!utils.isNilOrEmpty(appSettings))
    return <WebSDK onComplete={handleComplete} onError={handleError} />;
  return null;
};
